import type vtkImageData from '@kitware/vtk.js/Common/DataModel/ImageData';

import type { TypedArrayConstructorName } from '@/src/types';

import { vtkObject } from '@kitware/vtk.js/interfaces';
import vtk from '@kitware/vtk.js/vtk';

import { TypedArrayConstructorNames } from '@/src/utils';

const AllowedTypedArrays = new Set(TypedArrayConstructorNames);

function isTypedArrayName(name: string): name is TypedArrayConstructorName {
  return AllowedTypedArrays.has(name);
}

function isVtkObject(obj: unknown): obj is vtkObject {
  return typeof obj === 'object' && obj !== null && 'isA' in obj && typeof obj.isA === 'function';
}

function isImageData(obj: unknown): obj is vtkImageData {
  return isVtkObject(obj) && obj.isA('vtkImageData');
}

type ISerializedVtkObject = Parameters<typeof vtk>[0];

interface SerializedImageData extends ISerializedVtkObject {
  pointData: {
    arrays: Array<{
      data: {
        dataType: TypedArrayConstructorName;
        values: number[] | ArrayBufferView;
      };
    }>;
  };
}

function wrapValuesInTypedArray(serializedImageData: SerializedImageData) {
  // convert data values to a typed array for smaller packets.
  const { arrays } = serializedImageData.pointData;
  arrays.forEach((da) => {
    const { data } = da;
    if (isTypedArrayName(data.dataType)) {
      data.values = new globalThis[data.dataType](
        data.values as number[],
      );
    }
  });
  return serializedImageData;
}

export function serializeVtkImageData(obj: unknown): unknown {
  if (!isImageData(obj)) {
    return obj;
  }

  const serialized = obj.toJSON() as SerializedImageData;
  return wrapValuesInTypedArray(serialized);
}

export function deserializeVtkImageData(obj: unknown) {
  if (typeof obj !== 'object'
    || obj === null
    || !('vtkClass' in obj)
    || typeof obj.vtkClass !== 'string'
    || obj.vtkClass !== 'vtkImageData') {
    return obj;
  }

  try {
    return vtk(wrapValuesInTypedArray(obj as SerializedImageData));
  }
  catch /* (e) */ {
    return obj;
  }
}
